import clsx from 'clsx';

import CountdownBadges from './CountdownBadges';
import MiniMonth from './MiniMonth';
import { calendarPushStatus, type Account, type CalendarEvent, type Calendar } from '../types';
import { retintEventBackground, useResolvedTheme } from '../theme';

interface Props {
  anchor: Date;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  accounts: Account[];
  calendars: Calendar[];
  countdowns: CalendarEvent[];
  pushConfigured: boolean;
  selectedCalendarId?: string | null;
  onPickDate: (d: Date) => void;
  onPickCountdown: (e: CalendarEvent) => void;
  onRemoveCountdown: (id: string) => void;
  onPickCalendar: (id: string) => void;
  onToggleCalendar: (id: string, visible: boolean) => void;
  onPickCalendarColor: (id: string, rect: DOMRect) => void;
  onAddAccount: () => void;
  onOpenSettings: () => void;
  onOpenPlan: () => void;
  onCalendarContextMenu?: (id: string, x: number, y: number) => void;
}

export default function Sidebar({
  anchor,
  weekStartsOn = 0,
  accounts,
  calendars,
  countdowns,
  pushConfigured,
  selectedCalendarId,
  onPickDate,
  onPickCountdown,
  onRemoveCountdown,
  onPickCalendar,
  onToggleCalendar,
  onPickCalendarColor,
  onAddAccount,
  onOpenSettings,
  onOpenPlan,
  onCalendarContextMenu
}: Props) {
  const theme = useResolvedTheme();
  const groups = accounts.map((a) => ({
    account: a,
    cals: calendars.filter((c) => c.account_id === a.id && c.in_sidebar)
  }));

  return (
    <aside className="flex h-dvh w-[240px] flex-shrink-0 flex-col border-r border-[var(--border)] bg-[var(--bg-sunken)]">
      <MiniMonth anchor={anchor} weekStartsOn={weekStartsOn} onPick={onPickDate} />

      <div className="flex-1 overflow-y-auto p-2">
        {groups.map((g) => {
          const errored = g.account.status === 'error';
          return (
          <div key={g.account.id} className="mb-4">
            <div
              className={clsx(
                'overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1 text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]',
                errored && 'opacity-60'
              )}
            >
              {g.account.source_account_id}
            </div>
            {errored && (
              // Reconnect sits on its own row — the chip-inline-with-email
              // version got squeezed off in a 240px sidebar. Stays at full
              // opacity while the rest of the group is dimmed.
              <a
                href={`/google/connect?email=${encodeURIComponent(g.account.source_account_id)}`}
                title={g.account.error_message ?? 'Sign-in needs reconnecting'}
                className="mx-1 mb-1 flex items-center justify-center gap-[6px] rounded border border-[var(--today-fg)] px-2 py-[5px] text-[0.78rem] text-[var(--today-fg)] no-underline hover:bg-[var(--today-bg)]"
              >
                <span aria-hidden>↻</span> Reconnect
              </a>
            )}
            <div className={clsx(errored && 'opacity-60')}>
            {g.cals.map((c) => {
              const hidden = !c.visible;
              const selected = c.id === selectedCalendarId;
              // Per-calendar warnings are only meaningful when the parent
              // account is otherwise healthy — if the whole account is in
              // error, the Reconnect button above already speaks for the
              // entire group, and surfacing a yellow chip on each row would
              // be visual noise.
              const warnings: string[] = [];
              if (!errored) {
                if (c.last_error) warnings.push(`Sync: ${c.last_error}`);
                if (calendarPushStatus(c, pushConfigured) === 'degraded') {
                  warnings.push(
                    'Push notifications stopped — events arrive on the slow poll instead.'
                  );
                }
              }
              const warnTooltip = warnings.length ? warnings.join('\n\n') : null;
              return (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onPickCalendar(c.id)}
                  onContextMenu={(ev) => {
                    if (!onCalendarContextMenu) return;
                    ev.preventDefault();
                    onCalendarContextMenu(c.id, ev.clientX, ev.clientY);
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      onPickCalendar(c.id);
                    }
                  }}
                  className={clsx(
                    'group flex cursor-pointer items-center gap-2 rounded px-2 py-[6px] text-sm hover:bg-[var(--bg-hover)]',
                    hidden && 'text-[var(--text-faint)]',
                    selected && 'bg-[var(--bg-muted)]'
                  )}
                >
                  <button
                    type="button"
                    aria-label="change color"
                    title="Change color"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                      onPickCalendarColor(c.id, rect);
                    }}
                    className="block h-[14px] w-[14px] flex-shrink-0 rounded-[3px] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                    style={{
                      background: retintEventBackground(c.background_color, theme),
                      opacity: hidden ? 0.4 : 1
                    }}
                  />
                  <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                    {c.summary}
                  </span>
                  {warnTooltip && (
                    <span
                      aria-label="calendar warning"
                      title={warnTooltip}
                      className="flex-shrink-0 text-[0.78rem] leading-none text-[var(--today-fg)]"
                    >
                      ⚠
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onToggleCalendar(c.id, !c.visible);
                    }}
                    title={hidden ? 'Show events' : 'Hide events'}
                    aria-label={hidden ? 'show events' : 'hide events'}
                    className={clsx(
                      'rounded p-[2px] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]',
                      hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
                    )}
                  >
                    {hidden ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              );
            })}
            </div>
          </div>
          );
        })}

        {accounts.length === 0 && <p className="p-2 text-sm text-[var(--text-faint)]">No calendars yet.</p>}

        <button
          onClick={onAddAccount}
          className="mt-2 flex w-full items-center gap-2 rounded px-2 py-[8px] text-sm text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          <span className="grid h-[14px] w-[14px] place-items-center rounded-[3px] border border-dashed border-[var(--border-strong)] text-[10px] text-[var(--text-faint)]">
            +
          </span>
          Add calendar account
        </button>
      </div>

      <CountdownBadges
        events={countdowns}
        onPick={onPickCountdown}
        onRemove={onRemoveCountdown}
      />

      <div className="border-t border-[var(--border)] p-2">
        <button
          onClick={onOpenPlan}
          className="flex w-full items-center gap-2 rounded px-2 py-[8px] text-sm text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          <span className="w-4 text-center" aria-hidden>
            ◷
          </span>
          Plan
        </button>
        <button
          onClick={onOpenSettings}
          className="flex w-full items-center gap-2 rounded px-2 py-[8px] text-sm text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          <span className="w-4 text-center" aria-hidden>
            ⚙
          </span>
          Settings
        </button>
      </div>
    </aside>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1.5 8s2.3-4.5 6.5-4.5S14.5 8 14.5 8 12.2 12.5 8 12.5 1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2.5 4.5C1.5 5.7 1.5 8 1.5 8s2.3 4.5 6.5 4.5c1.1 0 2-.2 2.8-.6" />
      <path d="M6 3.8C6.6 3.6 7.3 3.5 8 3.5c4.2 0 6.5 4.5 6.5 4.5s-.5 1-1.5 2" />
      <path d="M6.5 9.5a2 2 0 0 1 3-3" />
      <path d="M2 2l12 12" />
    </svg>
  );
}
